import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { useDocumentState } from '../../../../../../contexts/DocumentContext';
import ConfirmReadyToSignModal from './ConfirmReadyToSignModal';
import { givePossessionToCounterparty } from '../../../../../../utils/api/give-possession-to-counterparty';
import { useNotifications } from '../../../../../../notifications';
import { flushActiveEditor } from '../../../../document-editor/flushActiveEditor';

interface ReadyToSignContainerProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  dealId: string;
  teamId: string;
  creatorTeamName?: string;
}

const ReadyToSignContainer = ({
  isOpen,
  onClose,
  documentId,
  dealId,
  teamId,
  creatorTeamName,
}: ReadyToSignContainerProps) => {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const { state } = useDocumentState();
  const documentTitle = state.title;

  const handleConfirm = useCallback(
    async (message: string) => {
      try {
        setIsSubmitting(true);

        // Land any edits still inside the autosave debounce, and reconcile the
        // relational suggestion rows the server gates on. This USED to be a
        // `window.editor` CKEditor call, which silently did nothing under the
        // DOCX-native editor — so a suggestion accepted seconds earlier was not
        // saved, and one still pending was not counted.
        const { pendingSuggestions } = await flushActiveEditor(documentId);

        // "Ready to sign" asserts the document is final, so it cannot be
        // asserted while tracked changes are unresolved. The server enforces
        // this too (GivePosessionToCounterpartyMutation); checking here turns a
        // rejected mutation into an explanation the user can act on.
        if (pendingSuggestions !== null && pendingSuggestions > 0) {
          notifyError({
            message: {
              id: 'ready-to-sign.pending-suggestions',
              defaultMessage:
                '{count, plural, one {# suggestion is} other {# suggestions are}} still unresolved. Accept or reject {count, plural, one {it} other {them}} before marking this document ready to sign.',
              values: { count: pendingSuggestions },
            },
          });
          setIsSubmitting(false);
          return;
        }

        // Call mutation with readyToSign flag (triggers refetchQueries)
        await givePossessionToCounterparty({
          documentId,
          additionalMessage: message,
          readyToSign: true,
        })(apolloClient);

        // Close modal
        onClose();

        // Navigate back to deal page first
        navigate(`/teams/${teamId}/deals/${dealId}`);

        // Show notification after navigation completes
        setTimeout(() => {
          notifySuccess({
            message: {
              id: 'ready-to-sign.success',
              defaultMessage: '{documentTitle} marked as ready to sign',
              values: { documentTitle },
            },
          });
        }, 300);
      } catch (error) {
        console.error('Error marking document as ready to sign:', error);
        notifyError({
          message: error instanceof Error ? error.message : 'Failed to mark document as ready to sign',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      documentId,
      dealId,
      teamId,
      documentTitle,
      apolloClient,
      notifySuccess,
      notifyError,
      onClose,
      navigate,
    ]
  );

  return (
    <ConfirmReadyToSignModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleConfirm}
      creatorTeamName={creatorTeamName}
      isSubmitting={isSubmitting}
    />
  );
};

export default ReadyToSignContainer;
